/**
 * Enhanced Split-Screen RC Upload Component
 * 
 * Features:
 * - Multi-file upload (up to 5 files)
 * - Split-screen layout (preview left, form right)
 * - Visual annotations with highlighting
 * - Real-time OCR processing
 * - Inline field editing
 * - File navigation/preview
 */

import { useState, useCallback } from 'react';
import { Upload, Loader2, CheckCircle, AlertCircle, X, ZoomIn, ZoomOut, FileText, ChevronLeft, ChevronRight, Eye, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { OCRService } from '@/lib/services/ocrService';
import type { RCExtractedData, FieldLocation } from '@lebusiness/types';

interface ProcessedFile {
  file: File;
  preview: string;
  status: 'pending' | 'processing' | 'success' | 'error';
  progress: number;
  extractedData?: RCExtractedData;
  fieldLocations?: FieldLocation[];
  error?: string;
}

interface SplitScreenRCUploadProps {
  onComplete: (data: RCExtractedData) => void;
  onError?: (error: string) => void;
}

const LEGAL_FORMS = [
  { value: 'SARL', label: 'SARL - Société à Responsabilité Limitée' },
  { value: 'EURL', label: 'EURL - Entreprise Unipersonnelle à Responsabilité Limitée' },
  { value: 'SPA', label: 'SPA - Société Par Actions' },
  { value: 'SNC', label: 'SNC - Société en Nom Collectif' },
  { value: 'SCS', label: 'SCS - Société en Commandite Simple' },
  { value: 'EI', label: 'EI - Entreprise Individuelle' },
  { value: 'AUTRE', label: 'Autre' },
];

const WILAYAS = [
  '01-Adrar', '02-Chlef', '03-Laghouat', '04-Oum El Bouaghi', '05-Batna',
  '06-Béjaïa', '07-Biskra', '08-Béchar', '09-Blida', '10-Bouira',
  '11-Tamanrasset', '12-Tébessa', '13-Tlemcen', '14-Tiaret', '15-Tizi Ouzou',
  '16-Alger', '17-Djelfa', '18-Jijel', '19-Sétif', '20-Saïda',
  '21-Skikda', '22-Sidi Bel Abbès', '23-Annaba', '24-Guelma', '25-Constantine',
  '26-Médéa', '27-Mostaganem', '28-M\'Sila', '29-Mascara', '30-Ouargla',
  '31-Oran', '32-El Bayadh', '33-Illizi', '34-Bordj Bou Arréridj', '35-Boumerdès',
  '36-El Tarf', '37-Tindouf', '38-Tissemsilt', '39-El Oued', '40-Khenchela',
  '41-Souk Ahras', '42-Tipaza', '43-Mila', '44-Aïn Defla', '45-Naâma',
  '46-Aïn Témouchent', '47-Ghardaïa', '48-Relizane', '49-Timimoun', '50-Bordj Badji Mokhtar',
  '51-Ouled Djellal', '52-Béni Abbès', '53-In Salah', '54-In Guezzam', '55-Touggourt',
  '56-Djanet', '57-El M\'Ghair', '58-El Meniaa',
];

const FIELD_COLORS: Record<string, string> = {
  company_name: '#3B82F6',    // Blue
  rc_number: '#10B981',       // Green
  nif: '#F59E0B',            // Orange
  nis: '#8B5CF6',            // Purple
  address: '#EC4899',        // Pink
  capital: '#14B8A6',        // Teal
  legal_form: '#EF4444',     // Red
};

const MAX_FILES = 5;

export function SplitScreenRCUpload({ onComplete, onError }: SplitScreenRCUploadProps) {
  const [files, setFiles] = useState<ProcessedFile[]>([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedAnnotation, setSelectedAnnotation] = useState<string | null>(null);
  const [isManualMode, setIsManualMode] = useState(false);
  const [manualData, setManualData] = useState<Partial<RCExtractedData>>({});

  const currentFile = files[currentFileIndex];

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    await handleFiles(droppedFiles);
  }, [files]);

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    await handleFiles(selectedFiles);
  };

  const handleFiles = async (selectedFiles: File[]) => {
    // Check max files limit
    if (files.length + selectedFiles.length > MAX_FILES) {
      onError?.(`Maximum ${MAX_FILES} fichiers autorisés`);
      return;
    }

    // Validate file types and sizes
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    const newFiles: ProcessedFile[] = [];

    for (const file of selectedFiles) {
      if (!validTypes.includes(file.type)) {
        onError?.(`Format non supporté: ${file.name}`);
        continue;
      }

      if (file.size > maxSize) {
        onError?.(`Fichier trop volumineux: ${file.name} (max 10MB)`);
        continue;
      }

      // Create preview
      let preview = '';
      if (file.type.startsWith('image/')) {
        preview = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.readAsDataURL(file);
        });
      }

      newFiles.push({
        file,
        preview,
        status: 'pending',
        progress: 0,
      });
    }

    setFiles([...files, ...newFiles]);

    // Start processing files
    newFiles.forEach((_, index) => {
      processFile(files.length + index);
    });
  };

  const processFile = async (index: number) => {
    setFiles(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], status: 'processing', progress: 10 };
      return updated;
    });

    // Simulate progress
    const progressInterval = setInterval(() => {
      setFiles(prev => {
        const updated = [...prev];
        if (updated[index].progress < 90) {
          updated[index] = { ...updated[index], progress: updated[index].progress + 10 };
        }
        return updated;
      });
    }, 500);

    // Timeout after 30 seconds
    const timeoutId = setTimeout(() => {
      clearInterval(progressInterval);
      setFiles(prev => {
        const updated = [...prev];
        if (updated[index].status === 'processing') {
          updated[index] = {
            ...updated[index],
            status: 'error',
            progress: 0,
            error: 'Délai d\'attente dépassé. Veuillez réessayer ou saisir manuellement.',
          };
        }
        return updated;
      });
    }, 30000);

    try {
      const file = files[index]?.file || (await new Promise<File>(resolve => {
        const check = () => {
          if (files[index]?.file) resolve(files[index].file);
          else setTimeout(check, 100);
        };
        check();
      }));

      // Perform OCR with timeout protection
      const result = await Promise.race([
        OCRService.extractFromRC(file),
        new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('OCR timeout')), 25000)
        )
      ]);

      clearInterval(progressInterval);
      clearTimeout(timeoutId);

      if (result.success && result.data) {
        setFiles(prev => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            status: 'success',
            progress: 100,
            extractedData: result.data,
            fieldLocations: result.fieldLocations || [],
            preview: result.pages?.[0]?.imageUrl || updated[index].preview,
          };
          return updated;
        });
      } else {
        throw new Error(result.error || 'Échec de l\'extraction');
      }
    } catch (error) {
      clearInterval(progressInterval);
      clearTimeout(timeoutId);
      const errorMsg = error instanceof Error ? error.message : 'Erreur OCR';
      
      setFiles(prev => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          status: 'error',
          progress: 0,
          error: errorMsg,
        };
        return updated;
      });
    }
  };

  const handleFieldChange = (field: keyof RCExtractedData, value: any) => {
    if (currentFile?.extractedData) {
      setFiles(prev => {
        const updated = [...prev];
        updated[currentFileIndex] = {
          ...updated[currentFileIndex],
          extractedData: {
            ...updated[currentFileIndex].extractedData!,
            [field]: value,
          },
        };
        return updated;
      });
    }
  };

  const handleComplete = () => {
    if (isManualMode) {
      onComplete(manualData as RCExtractedData);
    } else if (currentFile?.extractedData) {
      onComplete(currentFile.extractedData);
    }
  };

  const handleManualEntry = () => {
    setIsManualMode(true);
    setManualData({});
  };

  const handleManualFieldChange = (field: keyof RCExtractedData, value: any) => {
    setManualData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDeleteFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    if (currentFileIndex >= files.length - 1) {
      setCurrentFileIndex(Math.max(0, files.length - 2));
    }
  };

  const renderAnnotations = () => {
    if (!currentFile?.fieldLocations || currentFile.fieldLocations.length === 0) return null;

    return currentFile.fieldLocations.map((loc, index) => {
      const color = FIELD_COLORS[loc.field] || '#6B7280';
      const isSelected = selectedAnnotation === `${loc.field}-${index}`;
      
      return (
        <div
          key={`${loc.field}-${index}`}
          className={`
            absolute cursor-pointer transition-all
            ${isSelected ? 'z-20 scale-105' : 'z-10'}
          `}
          style={{
            left: `${loc.bbox.x}%`,
            top: `${loc.bbox.y}%`,
            width: `${loc.bbox.width}%`,
            height: `${loc.bbox.height}%`,
          }}
          onClick={() => setSelectedAnnotation(isSelected ? null : `${loc.field}-${index}`)}
          onMouseEnter={() => !isSelected && setSelectedAnnotation(`${loc.field}-${index}`)}
          onMouseLeave={() => !isSelected && setSelectedAnnotation(null)}
        >
          {/* Highlight rectangle */}
          <div
            className="absolute inset-0 border-2 rounded"
            style={{
              borderColor: color,
              backgroundColor: isSelected ? `${color}40` : `${color}20`,
            }}
          />
          
          {/* Label badge */}
          <div
            className={`
              absolute -top-7 left-0 rounded-md px-2 py-1 text-xs font-semibold text-white whitespace-nowrap shadow-lg
              ${isSelected ? 'scale-110' : ''}
            `}
            style={{ backgroundColor: color }}
          >
            {loc.label} ({Math.round(loc.confidence)}%)
          </div>

          {/* Value tooltip */}
          {isSelected && (
            <div
              className="absolute top-full left-0 mt-1 rounded-md bg-gray-900 px-3 py-2 text-sm text-white shadow-xl whitespace-nowrap max-w-xs"
            >
              <div className="font-semibold">{loc.label}</div>
              <div className="text-gray-300">{loc.text}</div>
              <div className="text-xs text-gray-400 mt-1">
                Confiance: {Math.round(loc.confidence)}%
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  const renderFileList = () => {
    if (files.length === 0) return null;

    return (
      <div className="flex gap-2 overflow-x-auto pb-2">
        {files.map((file, index) => (
          <div
            key={index}
            className={`
              relative flex-shrink-0 cursor-pointer rounded-lg border-2 p-2 transition-all
              ${currentFileIndex === index ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-primary/50'}
              ${file.status === 'error' ? 'border-red-500' : ''}
              ${file.status === 'success' ? 'border-green-500' : ''}
            `}
            onClick={() => setCurrentFileIndex(index)}
          >
            {/* Status badge */}
            <div className="absolute -top-2 -right-2 z-10">
              {file.status === 'processing' && (
                <div className="rounded-full bg-blue-500 p-1">
                  <Loader2 className="h-3 w-3 animate-spin text-white" />
                </div>
              )}
              {file.status === 'success' && (
                <div className="rounded-full bg-green-500 p-1">
                  <CheckCircle className="h-3 w-3 text-white" />
                </div>
              )}
              {file.status === 'error' && (
                <div className="rounded-full bg-red-500 p-1">
                  <AlertCircle className="h-3 w-3 text-white" />
                </div>
              )}
            </div>

            {/* Delete button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteFile(index);
              }}
              className="absolute -top-2 -left-2 z-10 rounded-full bg-red-500 p-1 hover:bg-red-600"
            >
              <X className="h-3 w-3 text-white" />
            </button>

            {/* Preview or icon */}
            <div className="h-16 w-16 rounded overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
              {file.preview ? (
                <img src={file.preview} alt={file.file.name} className="h-full w-full object-cover" />
              ) : (
                <FileText className="h-8 w-8 text-gray-400" />
              )}
            </div>

            {/* File info */}
            <div className="mt-1 text-xs">
              <p className="font-medium truncate w-16" title={file.file.name}>
                {file.file.name}
              </p>
              <p className="text-gray-500">
                {(file.file.size / 1024 / 1024).toFixed(1)} MB
              </p>
              {file.status === 'processing' && (
                <div className="mt-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 transition-all"
                    style={{ width: `${file.progress}%` }}
                  />
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Add more button */}
        {files.length < MAX_FILES && (
          <div
            className="flex-shrink-0 cursor-pointer rounded-lg border-2 border-dashed border-gray-300 p-2 hover:border-primary transition-all"
            onClick={() => document.getElementById('file-input')?.click()}
          >
            <div className="h-16 w-16 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
              <Upload className="h-8 w-8 text-gray-400" />
            </div>
            <p className="mt-1 text-xs text-center text-gray-500">Ajouter</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* File List */}
      {renderFileList()}

      {/* Split Screen */}
      <div className="grid grid-cols-2 gap-6 h-[calc(100vh-350px)] min-h-[600px]">
        {/* LEFT SIDE: Document Preview */}
        <div className="flex flex-col space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Document {files.length > 0 && `(${currentFileIndex + 1}/${files.length})`}
            </h3>
            {currentFile?.preview && (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium min-w-[60px] text-center">
                  {Math.round(zoom * 100)}%
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setZoom(Math.min(2, zoom + 0.1))}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Upload Area or Preview */}
          {files.length === 0 && !isManualMode ? (
            <div
              className={`
                flex-1 flex items-center justify-center rounded-lg border-2 border-dashed transition-all
                ${isDragging ? 'border-primary bg-primary/5 scale-[1.02]' : 'border-gray-300 dark:border-gray-700'}
              `}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="text-center p-8">
                <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
                  <Upload className="h-10 w-10 text-primary" />
                </div>
                <h3 className="mb-2 text-lg font-semibold">Télécharger vos documents RC</h3>
                <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                  Glissez-déposez ou cliquez pour sélectionner (jusqu'à {MAX_FILES} fichiers)
                </p>
                <Button
                  type="button"
                  onClick={() => document.getElementById('file-input')?.click()}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Sélectionner fichiers
                </Button>
                <input
                  id="file-input"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,application/pdf"
                  multiple
                  className="hidden"
                  onChange={handleFileInput}
                />
                <p className="mt-4 text-xs text-gray-500">
                  PDF, JPG, PNG - Maximum 10 MB par fichier
                </p>
                
                {/* Manual Entry Option */}
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">OU</p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleManualEntry}
                  >
                    ✏️ Saisie manuelle (sans upload)
                  </Button>
                  <p className="mt-2 text-xs text-gray-500">
                    Remplissez les informations manuellement
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-auto rounded-lg border bg-gray-50 dark:bg-gray-900 p-4 relative">
              {currentFile?.status === 'processing' && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/90 dark:bg-gray-900/90 z-30">
                  <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                  <p className="text-sm font-medium mb-2">Extraction en cours...</p>
                  <div className="w-64 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${currentFile.progress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">{currentFile.progress}%</p>
                </div>
              )}

              {currentFile?.preview && (
                <div 
                  className="relative inline-block" 
                  style={{ 
                    transform: `scale(${zoom})`, 
                    transformOrigin: 'top left',
                    minWidth: '100%',
                  }}
                >
                  <img
                    src={currentFile.preview}
                    alt="Document preview"
                    className="max-w-full rounded"
                  />
                  {renderAnnotations()}
                </div>
              )}

              {currentFile?.status === 'error' && (
                <div className="flex flex-col items-center justify-center h-full">
                  <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
                  <p className="text-sm font-medium text-red-700 mb-4 text-center px-4">{currentFile.error}</p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => processFile(currentFileIndex)}
                    >
                      Réessayer
                    </Button>
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      onClick={handleManualEntry}
                    >
                      ✏️ Saisie manuelle
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Navigation */}
          {files.length > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={currentFileIndex === 0}
                onClick={() => setCurrentFileIndex(currentFileIndex - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">
                {currentFileIndex + 1} / {files.length}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={currentFileIndex === files.length - 1}
                onClick={() => setCurrentFileIndex(currentFileIndex + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {/* RIGHT SIDE: Extracted Fields Form */}
        <div className="flex flex-col space-y-4 overflow-auto">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Données extraites
          </h3>

          {!currentFile?.extractedData && !isManualMode ? (
            <div className="flex-1 flex items-center justify-center rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
              <div className="text-center p-8">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                  <AlertCircle className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {files.length === 0 
                    ? 'Téléchargez un document RC pour voir les données extraites'
                    : 'En attente de l\'extraction...'}
                </p>
                {files.length === 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleManualEntry}
                  >
                    ✏️ Saisie manuelle
                  </Button>
                )}
              </div>
            </div>
          ) : isManualMode ? (
            <div className="space-y-4 flex-1 overflow-auto pr-2">
              {/* Manual Entry Form */}
              <div className="rounded-lg bg-blue-50 dark:bg-blue-950 p-4 border border-blue-200 dark:border-blue-800 mb-4">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  ✏️ Mode de saisie manuelle
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                  Remplissez tous les champs ci-dessous
                </p>
              </div>

              {/* RC Number */}
              <div>
                <Label htmlFor="manual_rc_number">Numéro RC *</Label>
                <Input
                  id="manual_rc_number"
                  value={manualData.rc_number || ''}
                  onChange={(e) => handleManualFieldChange('rc_number', e.target.value)}
                  placeholder="XX/XX-XXXXXXX"
                  className="mt-1"
                />
              </div>

              {/* Company Name (French) */}
              <div>
                <Label htmlFor="manual_company_name_fr">Raison Sociale (Français) *</Label>
                <Input
                  id="manual_company_name_fr"
                  value={manualData.company_name_fr || ''}
                  onChange={(e) => handleManualFieldChange('company_name_fr', e.target.value)}
                  placeholder="Ex: ABC Trading SARL"
                  className="mt-1"
                />
              </div>

              {/* Company Name (Arabic) */}
              <div>
                <Label htmlFor="manual_company_name_ar">الاسم التجاري (عربي)</Label>
                <Input
                  id="manual_company_name_ar"
                  value={manualData.company_name_ar || ''}
                  onChange={(e) => handleManualFieldChange('company_name_ar', e.target.value)}
                  placeholder="مثال: ABC Trading"
                  dir="rtl"
                  className="mt-1"
                />
              </div>

              {/* NIF */}
              <div>
                <Label htmlFor="manual_nif">NIF *</Label>
                <Input
                  id="manual_nif"
                  value={manualData.nif || ''}
                  onChange={(e) => handleManualFieldChange('nif', e.target.value)}
                  placeholder="001234567891234"
                  className="mt-1"
                />
              </div>

              {/* NIS */}
              <div>
                <Label htmlFor="manual_nis">NIS</Label>
                <Input
                  id="manual_nis"
                  value={manualData.nis || ''}
                  onChange={(e) => handleManualFieldChange('nis', e.target.value)}
                  placeholder="001234567891234"
                  className="mt-1"
                />
              </div>

              {/* Legal Form */}
              <div>
                <Label htmlFor="manual_legal_form">Forme Juridique *</Label>
                <Select
                  value={manualData.legal_form || ''}
                  onValueChange={(value) => handleManualFieldChange('legal_form', value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Sélectionnez" />
                  </SelectTrigger>
                  <SelectContent>
                    {LEGAL_FORMS.map((form) => (
                      <SelectItem key={form.value} value={form.value}>
                        {form.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Address */}
              <div>
                <Label htmlFor="manual_address">Adresse *</Label>
                <Input
                  id="manual_address"
                  value={manualData.address || ''}
                  onChange={(e) => handleManualFieldChange('address', e.target.value)}
                  placeholder="123 Rue de la République"
                  className="mt-1"
                />
              </div>

              {/* Wilaya */}
              <div>
                <Label htmlFor="manual_wilaya">Wilaya *</Label>
                <Select
                  value={manualData.wilaya || ''}
                  onValueChange={(value) => handleManualFieldChange('wilaya', value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Sélectionnez" />
                  </SelectTrigger>
                  <SelectContent>
                    {WILAYAS.map((wilaya) => (
                      <SelectItem key={wilaya} value={wilaya}>
                        {wilaya}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Capital */}
              <div>
                <Label htmlFor="manual_capital">Capital Social (DZD) *</Label>
                <Input
                  id="manual_capital"
                  type="number"
                  value={manualData.capital || ''}
                  onChange={(e) => handleManualFieldChange('capital', parseFloat(e.target.value))}
                  placeholder="1000000"
                  className="mt-1"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 sticky bottom-0 bg-white dark:bg-gray-950 pb-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsManualMode(false);
                    setManualData({});
                  }}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  type="button"
                  onClick={handleComplete}
                  className="flex-1"
                  disabled={!manualData.rc_number || !manualData.company_name_fr || !manualData.nif}
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Confirmer
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 flex-1 overflow-auto pr-2">
              {/* RC Number */}
              <div>
                <Label htmlFor="rc_number" className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded" style={{ backgroundColor: FIELD_COLORS.rc_number }} />
                  Numéro RC
                </Label>
                <Input
                  id="rc_number"
                  value={currentFile.extractedData.rc_number || ''}
                  onChange={(e) => handleFieldChange('rc_number', e.target.value)}
                  placeholder="XX/XX-XXXXXXX"
                  className="mt-1"
                />
              </div>

              {/* Company Name (French) */}
              <div>
                <Label htmlFor="company_name_fr" className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded" style={{ backgroundColor: FIELD_COLORS.company_name }} />
                  Raison Sociale (Français)
                </Label>
                <Input
                  id="company_name_fr"
                  value={currentFile.extractedData.company_name_fr || ''}
                  onChange={(e) => handleFieldChange('company_name_fr', e.target.value)}
                  placeholder="Ex: ABC Trading SARL"
                  className="mt-1"
                />
              </div>

              {/* Company Name (Arabic) */}
              <div>
                <Label htmlFor="company_name_ar" className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded" style={{ backgroundColor: FIELD_COLORS.company_name }} />
                  الاسم التجاري (عربي)
                </Label>
                <Input
                  id="company_name_ar"
                  value={currentFile.extractedData.company_name_ar || ''}
                  onChange={(e) => handleFieldChange('company_name_ar', e.target.value)}
                  placeholder="مثال: ABC Trading"
                  dir="rtl"
                  className="mt-1"
                />
              </div>

              {/* NIF */}
              <div>
                <Label htmlFor="nif" className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded" style={{ backgroundColor: FIELD_COLORS.nif }} />
                  NIF
                </Label>
                <Input
                  id="nif"
                  value={currentFile.extractedData.nif || ''}
                  onChange={(e) => handleFieldChange('nif', e.target.value)}
                  placeholder="001234567891234"
                  className="mt-1"
                />
              </div>

              {/* NIS */}
              <div>
                <Label htmlFor="nis" className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded" style={{ backgroundColor: FIELD_COLORS.nis }} />
                  NIS
                </Label>
                <Input
                  id="nis"
                  value={currentFile.extractedData.nis || ''}
                  onChange={(e) => handleFieldChange('nis', e.target.value)}
                  placeholder="001234567891234"
                  className="mt-1"
                />
              </div>

              {/* Legal Form */}
              <div>
                <Label htmlFor="legal_form" className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded" style={{ backgroundColor: FIELD_COLORS.legal_form }} />
                  Forme Juridique
                </Label>
                <Select
                  value={currentFile.extractedData.legal_form || ''}
                  onValueChange={(value) => handleFieldChange('legal_form', value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Sélectionnez" />
                  </SelectTrigger>
                  <SelectContent>
                    {LEGAL_FORMS.map((form) => (
                      <SelectItem key={form.value} value={form.value}>
                        {form.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Address */}
              <div>
                <Label htmlFor="address" className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded" style={{ backgroundColor: FIELD_COLORS.address }} />
                  Adresse
                </Label>
                <Input
                  id="address"
                  value={currentFile.extractedData.address || ''}
                  onChange={(e) => handleFieldChange('address', e.target.value)}
                  placeholder="123 Rue de la République"
                  className="mt-1"
                />
              </div>

              {/* Wilaya */}
              <div>
                <Label htmlFor="wilaya">Wilaya</Label>
                <Select
                  value={currentFile.extractedData.wilaya || ''}
                  onValueChange={(value) => handleFieldChange('wilaya', value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Sélectionnez" />
                  </SelectTrigger>
                  <SelectContent>
                    {WILAYAS.map((wilaya) => (
                      <SelectItem key={wilaya} value={wilaya}>
                        {wilaya}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Capital */}
              <div>
                <Label htmlFor="capital" className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded" style={{ backgroundColor: FIELD_COLORS.capital }} />
                  Capital Social (DZD)
                </Label>
                <Input
                  id="capital"
                  type="number"
                  value={currentFile.extractedData.capital || ''}
                  onChange={(e) => handleFieldChange('capital', parseFloat(e.target.value))}
                  placeholder="1000000"
                  className="mt-1"
                />
              </div>

              {/* Confidence Score */}
              {currentFile.extractedData.confidence_score && (
                <div className="rounded-lg bg-green-50 dark:bg-green-950 p-4 border border-green-200 dark:border-green-800">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="text-sm font-semibold text-green-900 dark:text-green-100">
                        Confiance globale: {Math.round(currentFile.extractedData.confidence_score * 100)}%
                      </p>
                      <p className="text-xs text-green-700 dark:text-green-300">
                        Vérifiez les champs avec une confiance faible
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 sticky bottom-0 bg-white dark:bg-gray-950 pb-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setFiles([]);
                    setCurrentFileIndex(0);
                  }}
                  className="flex-1"
                >
                  Tout annuler
                </Button>
                <Button
                  type="button"
                  onClick={handleComplete}
                  className="flex-1"
                  disabled={!currentFile.extractedData}
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Confirmer
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hidden file input for additional uploads */}
      <input
        id="file-input"
        type="file"
        accept="image/jpeg,image/jpg,image/png,application/pdf"
        multiple
        className="hidden"
        onChange={handleFileInput}
      />

      {/* Legend */}
      {currentFile?.fieldLocations && currentFile.fieldLocations.length > 0 && (
        <div className="rounded-lg bg-gray-50 dark:bg-gray-900 p-4 border">
          <h4 className="text-sm font-semibold mb-3">🎨 Légende des annotations</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(FIELD_COLORS).map(([field, color]) => (
              <div key={field} className="flex items-center gap-2">
                <div className="h-3 w-3 rounded" style={{ backgroundColor: color }} />
                <span className="text-xs capitalize">
                  {field.replace('_', ' ')}
                </span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            💡 Survolez ou cliquez sur les annotations pour voir plus de détails
          </p>
        </div>
      )}
    </div>
  );
}
